import { useEffect } from 'react'

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import { Menu, MenuButton, MenuList, useDisclosure } from '@chakra-ui/react'

import { FloorI } from '../types/types'
import { FloorDropdownItem } from './FloorDropdownItem'

interface FloorDropdownIProps {
  floors: FloorI[]
  selectedFloorName: string
  selectedFloorId: string
  onItemClick: (id: string) => void
}

export const FloorDropdown = ({
  floors,
  selectedFloorName,
  selectedFloorId,
  onItemClick,
}: FloorDropdownIProps) => {
  const handleFloorItemClick = (id: string) => {
    onItemClick(id)
  }

  // Floors haven't loaded yet (or the facility genuinely has none) — opening
  // the menu would show an empty panel, which reads as the dropdown being
  // broken/hidden rather than as "nothing to select yet" (ENG-33730).
  const hasFloors = !!floors?.length

  // Controlled so an already-open menu can be force-closed below — disabling
  // the trigger only blocks a NEW open, and doesn't touch a menu that was
  // already open when floors emptied out mid-session (e.g. a facility/floor
  // switch clearing the list while the menu was still open).
  const { isOpen, onOpen, onClose } = useDisclosure()
  useEffect(() => {
    if (isOpen && !hasFloors) onClose()
  }, [isOpen, hasFloors, onClose])

  return (
    <Menu
      autoSelect={false}
      gutter={0}
      isOpen={isOpen}
      matchWidth
      onClose={onClose}
      onOpen={onOpen}
    >
      {() => (
        <>
          <MenuButton
            _hover={{ bg: '#F7F8F9' }}
            borderRadius='md'
            fontSize='20px'
            fontWeight='extrabold'
            isDisabled={!hasFloors}
            letterSpacing='-0.6px'
            mr='15px'
            px='15px'
            py='10px'
            shadow={isOpen && '0px 2px 6px 2px rgba(0, 0, 0, 0.1)'}
            transition='all 0.2s'
          >
            {selectedFloorName}
            {isOpen ? (
              <ChevronUpIcon ml='15px' />
            ) : (
              <ChevronDownIcon ml='15px' />
            )}
          </MenuButton>
          <MenuList maxH='40vh' minW='50px' overflowY='scroll'>
            {floors?.map((floor) => (
              <FloorDropdownItem
                key={floor.id}
                onClick={() => handleFloorItemClick(floor.id)}
                selected={selectedFloorId === floor.id}
                title={floor.label}
              />
            ))}
          </MenuList>
        </>
      )}
    </Menu>
  )
}
